import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { BandeauAlerte } from '../../components/index.js';
import { useChargement } from '../../hooks/useChargement.js';
import { ErreurApi, api } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';

type EtatDiffusion = 'a_envoyer' | 'a_jour' | 'modifie' | 'sans_email' | 'en_attente' | 'echec';

interface Ligne {
  id: string;
  nom: string;
  email: string | null;
  /** Tous les destinataires du parcours (une personne = un e-mail). */
  emails: string[];
  membres: string[];
  etat: EtatDiffusion;
  parcoursEnvoyeAt: string | null;
  dernierJob: { status: string; sentAt: string | null; createdAt: string; lastError: string | null } | null;
}

interface Donnees {
  pret: boolean;
  lignes: Ligne[];
  compte: { aEnvoyer: number; modifies: number; aJour: number; sansEmail: number; enAttente: number; echecs: number };
  dernierEnvoi: string | null;
  surprise: number | null;
}

const LIBELLES: Record<EtatDiffusion, string> = {
  a_envoyer: 'À envoyer',
  a_jour: 'À jour',
  modifie: 'Parcours modifié',
  sans_email: "Sans e-mail",
  en_attente: "En cours d'envoi",
  echec: "Échec d'envoi",
};

function dateHeure(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

/** Écran 8 (non dessiné) : diffusion des parcours par e-mail, suivi des envois. */
export function Diffusion() {
  const { detail } = useOutletContext<ContexteEvenement>();
  const eventId = detail.event.id;
  const { donnees, erreur, remplacer, recharger } = useChargement(() => api.get<Donnees>(`/api/events/${eventId}/diffusion`), [eventId]);
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  const envoyer = async (corps: { tous?: boolean; foyerIds?: string[] }) => {
    if (!donnees) return;
    setErreurAction(null);
    setMessage(null);
    setEnCours(true);
    try {
      const r = await api.post<{ envoyes: number; lignes: Ligne[]; compte: Donnees['compte']; dernierEnvoi: string | null }>(`/api/events/${eventId}/diffusion`, corps);
      remplacer({ ...donnees, lignes: r.lignes, compte: r.compte, dernierEnvoi: r.dernierEnvoi });
      setMessage(r.envoyes ? `${r.envoyes} parcours mis en file d'envoi. Les e-mails partent dans la minute.` : 'Rien à envoyer : tous les parcours sont à jour.');
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? e.message : 'Impossible de joindre le serveur.');
    } finally {
      setEnCours(false);
    }
  };

  if (erreur) return <BandeauAlerte niveau="attention" role="alert" titre={erreur} />;
  if (!donnees) return <p className="texte-muet">Chargement…</p>;

  const { compte } = donnees;
  const aEnvoyer = compte.aEnvoyer + compte.echecs;

  return (
    <div className="page-app">
      <div className="entete-page">
        <div style={{ maxWidth: 720 }}>
          <h1 className="display">Diffusion</h1>
          <p className="texte-doux">Chaque voiture reçoit par e-mail son parcours : ses trois tables, le nombre de convives si elle reçoit, et un lien vers sa page de la soirée. Le même lien part à chaque personne de la voiture qui a donné son e-mail. Les adresses ne sont partagées qu'aux convives concernés.</p>
        </div>
        <div className="ligne" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={() => recharger()} disabled={enCours}>
            Actualiser
          </button>
          <button type="button" className="btn principal" onClick={() => envoyer({})} disabled={enCours || !donnees.pret || aEnvoyer === 0}>
            {enCours ? 'Envoi…' : `Envoyer les parcours${aEnvoyer ? ` (${aEnvoyer})` : ''}`}
          </button>
        </div>
      </div>

      {!donnees.pret ? (
        <BandeauAlerte niveau="attention" titre="Pas encore de répartition." actions={<Link className="btn" to={`/app/e/${eventId}/repartition`}>Aller à la répartition</Link>}>
          Les parcours se construisent à partir des tables.
        </BandeauAlerte>
      ) : null}
      {erreurAction ? <BandeauAlerte niveau="attention" role="alert" titre={erreurAction} /> : null}
      {message ? <BandeauAlerte niveau="ok" role="status" titre={message} /> : null}
      {detail.planning.planningObsolete ? (
        <BandeauAlerte niveau="sombre" titre="Le planning n'est plus à jour." actions={<Link className="btn" to={`/app/e/${eventId}/repartition`}>Régénérer la répartition</Link>}>
          Les parcours déjà envoyés restent valables tant que vous ne régénérez pas. Après régénération, seuls les participants dont le parcours change recevront un nouvel e-mail.
        </BandeauAlerte>
      ) : null}

      <div className="cartes-stats">
        <div className={`carte stat${aEnvoyer ? ' attention' : ''}`}>
          <span className="valeur">{aEnvoyer}</span>
          <span className="libelle">à envoyer</span>
          <span className="detail">
            {compte.modifies ? `dont ${compte.modifies} parcours modifié${compte.modifies > 1 ? 's' : ''}` : 'jamais envoyés ou modifiés'}
            {compte.echecs ? ` · ${compte.echecs} en échec` : ''}
          </span>
        </div>
        <div className="carte stat">
          <span className="valeur">{compte.aJour}</span>
          <span className="libelle">à jour</span>
          <span className="detail">{donnees.dernierEnvoi ? `dernier envoi le ${dateHeure(donnees.dernierEnvoi)}` : 'aucun envoi pour le moment'}</span>
        </div>
        <div className="carte stat">
          <span className="valeur">{compte.enAttente}</span>
          <span className="libelle">en cours d'envoi</span>
          <span className="detail">file traitée toutes les 15 secondes</span>
        </div>
        <div className={`carte stat${compte.sansEmail ? ' attention' : ''}`}>
          <span className="valeur">{compte.sansEmail}</span>
          <span className="libelle">sans e-mail</span>
          <span className="detail">
            <Link to={`/app/e/${eventId}/planning`}>remettez-leur la fiche imprimée</Link>
          </span>
        </div>
      </div>

      {donnees.surprise !== null ? (
        <BandeauAlerte niveau="info" titre="Adresses surprises.">
          L'e-mail ne contient pas les adresses des hôtes : chacune se dévoile sur la page du parcours {donnees.surprise} min avant l'étape.
        </BandeauAlerte>
      ) : null}

      <section className="carte" style={{ padding: 0 }} aria-label="Suivi des envois">
        <div className="tableau-foyers" role="table" aria-label="Envois">
          <div className="trow trow-diffusion th" role="row">
            <span role="columnheader">Voiture</span>
            <span role="columnheader">E-mail</span>
            <span role="columnheader">État</span>
            <span role="columnheader">Dernier envoi</span>
            <span role="columnheader">
              <span className="sr-only">Actions</span>
            </span>
          </div>
          {donnees.lignes.map((l) => (
            <div key={l.id} className="trow trow-diffusion" role="row">
              <div role="cell">
                <strong>{l.nom}</strong>
                <div className="texte-muet" style={{ fontSize: 13 }}>
                  {l.membres.join(', ')}
                </div>
              </div>
              <div role="cell" className="texte-doux">
                {l.emails.length ? l.emails.map((e) => <div key={e}>{e}</div>) : <span className="texte-muet">—</span>}
              </div>
              <div role="cell">
                <span className={`etat-diffusion ${l.etat}`}>{LIBELLES[l.etat]}</span>
                {l.etat === 'echec' && l.dernierJob?.lastError ? (
                  <div className="texte-muet" style={{ fontSize: 12 }}>
                    {l.dernierJob.lastError.slice(0, 80)}
                  </div>
                ) : null}
              </div>
              <div role="cell" className="texte-doux">
                {l.dernierJob?.sentAt ? dateHeure(l.dernierJob.sentAt) : l.parcoursEnvoyeAt ? `${dateHeure(l.parcoursEnvoyeAt)} (en file)` : '—'}
              </div>
              <div role="cell" className="ligne" style={{ gap: 4 }}>
                <Link className="btn petit" to={`/app/e/${eventId}/parcours/${l.id}`}>
                  Aperçu
                </Link>
                {l.emails.length && donnees.pret ? (
                  <button type="button" className="btn petit" onClick={() => envoyer({ foyerIds: [l.id] })} disabled={enCours}>
                    {l.etat === 'a_jour' ? 'Renvoyer' : 'Envoyer'}
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div className="pied-tableau texte-muet">
          <span>Un nouvel envoi remplace le lien précédent de la voiture.</span>
          <button type="button" className="btn petit" onClick={() => envoyer({ tous: true })} disabled={enCours || !donnees.pret}>
            Tout renvoyer
          </button>
        </div>
      </section>
    </div>
  );
}
